import React, { useState } from "react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";

/**
 * About Pulse — the "who we are" section.
 * Distinct role vs Positioning (what we believe), Selected Work (what we've done)
 * and Ecosystem (the wider REFORM capabilities behind us).
 *
 * The image container renders a graceful branded fallback if the source fails to
 * load — so the section can never appear broken again while awaiting real
 * REFORM Pulse team behind-the-scenes photography.
 */
// Verified BTS: "Crew setting up lights on a film set" — free Unsplash license.
// Depicts three crew members actively rigging lighting equipment. Placeholder until
// real REFORM Pulse team behind-the-scenes photography is provided.
const ABOUT_IMAGE_SRC =
    "https://images.unsplash.com/photo-1757495022684-176ae0391ad8?auto=format&fit=crop&w=1200&q=80";
const ABOUT_IMAGE_ALT =
    "REFORM Pulse team working behind the scenes on an event production";

export default function AboutSection() {
    const [imageFailed, setImageFailed] = useState(false);

    return (
        <section className="section-pad relative" data-testid="home-about-section">
            <div className="container-editorial">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-5">
                        <div
                            className="editorial-image relative rounded-2xl overflow-hidden"
                            style={{ aspectRatio: "4 / 5" }}
                            data-testid="home-about-image-container"
                        >
                            {!imageFailed ? (
                                <img
                                    src={ABOUT_IMAGE_SRC}
                                    alt={ABOUT_IMAGE_ALT}
                                    loading="lazy"
                                    decoding="async"
                                    onError={() => setImageFailed(true)}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{ objectPosition: "center 45%" }}
                                />
                            ) : (
                                <div
                                    className="absolute inset-0 flex flex-col justify-end p-6 bg-gradient-to-br from-[#160607] via-[#0B0C10] to-[#0B0C10]"
                                    role="img"
                                    aria-label={ABOUT_IMAGE_ALT}
                                >
                                    <div className="absolute top-6 left-6 h-2 w-2 rounded-full bg-[#FF2A2A] animate-pulse-glow" />
                                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/50 relative">
                                        Behind the scenes
                                    </div>
                                    <div className="font-display text-2xl text-white leading-none mt-2 relative">
                                        Real people • real production
                                    </div>
                                </div>
                            )}

                            {/* Editorial overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 pointer-events-none" />
                            <div className="absolute bottom-6 left-6 right-6 z-10 pointer-events-none">
                                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/70 mb-1">
                                    On location — Kuala Lumpur
                                </div>
                                <div className="font-display text-2xl text-white leading-none">
                                    Behind the scenes
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <SectionEyebrow index="08" label="About Pulse" />
                        <Reveal>
                            <h2 className="display-2 text-white text-balance">
                                The live experience arm
                                <br />
                                of <span className="text-[#FF2A2A]">REFORM Group.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="body-lg mt-6">
                                REFORM Pulse creates culture-led brand experiences for organisations that want more than a functional event.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="body-md mt-4">
                                Backed by REFORM Group, we connect experiential strategy with creators, content, production, PR and digital amplification&mdash;giving each event a clear reason to exist and a life beyond the room.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="body-md mt-4 text-white/85">
                                Based in <span className="text-white">Kuala Lumpur</span>. Built for <span className="text-white">Malaysia and Southeast Asia</span>.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
